import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { NotificationIcon } from '@modernloop/shared/icons';
import { Badge, Button, IconButton, Popover, Tooltip, Typography, useTheme } from '@mui/material';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { Routes } from 'src/constants/routes';

import {
  EmployeeNotificationMedium,
  useEmployeeNotificationReceivedSubscription,
  useEmployeeNotificationPrefUpdateMutation,
  useEmployeeNotificationsLazyQuery,
  useMarkAllNotificationsReadMutation,
  useMarkAllNotificationsSeenMutation,
  useMarkNotificationReadMutation,
  useMarkNotificationUnreadMutation,
} from 'src/generated/mloop-graphql';

import useHistory from 'src/hooks/useHistory';
import { AccountSettingsTab, getAccountSettingUrls } from 'src/urls/getAccountSettingUrls';

import NotificationList from './NotificationList';
import {
  getNotificationTypesForLabelKey,
  mapEmployeeNotificationToView,
  NOTIFICATION_TYPE_LABEL_KEYS,
} from './notificationMapping';
import type { InboxNotificationView } from './types';

type NotificationInboxProps = {
  expanded?: boolean;
};

/** Fetches, updates, and routes notification inbox data around its presentational list. */
const NotificationInbox: React.FC<NotificationInboxProps> = ({ expanded = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [unseenCount, setUnseenCount] = useState(0);
  const [fetchNotifications, { data, loading }] = useEmployeeNotificationsLazyQuery({ fetchPolicy: 'network-only' });
  const [markRead] = useMarkNotificationReadMutation();
  const [markUnread] = useMarkNotificationUnreadMutation();
  const [updateNotificationPreference] = useEmployeeNotificationPrefUpdateMutation();
  const [markAllRead] = useMarkAllNotificationsReadMutation();
  const [markAllSeen] = useMarkAllNotificationsSeenMutation();

  const notifications = useMemo<InboxNotificationView[]>(
    () =>
      (data?.employeeNotifications.items ?? []).map((notification) => mapEmployeeNotificationToView(t, notification)),
    [data?.employeeNotifications.items, t]
  );
  const unreadCount = data?.employeeNotifications.unreadCount ?? 0;

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- This initial request synchronizes the external notification badge count.
  useEffect(() => {
    const fetchInitialBadgeCount = async () => {
      try {
        const result = await fetchNotifications({ variables: { input: { pageInput: { limit: 1 } } } });
        setUnseenCount(result.data?.employeeNotifications.unseenCount ?? 0);
      } catch {
        enqueueSnackbar(t('hiring-dashboard-nav:nav.notificationInbox.actionFailed'), { variant: 'error' });
      }
    };

    void fetchInitialBadgeCount();
  }, [fetchNotifications]);

  useEmployeeNotificationReceivedSubscription({
    onData: ({ data: subData }) => {
      if (!subData.data?.employeeNotificationReceived) {
        return;
      }

      setUnseenCount((previousCount) => previousCount + 1);
      // Subscription messages omit the full notification entity, so refresh only the visible inbox list.
      if (anchorEl) {
        void fetchNotifications({ variables: { input: { pageInput: { limit: 50 } } } });
      }
    },
  });

  const handleOpen = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
      void fetchNotifications({ variables: { input: { pageInput: { limit: 50 } } } });

      try {
        await markAllSeen();
        setUnseenCount(0);
      } catch {
        enqueueSnackbar(t('hiring-dashboard-nav:nav.notificationInbox.actionFailed'), { variant: 'error' });
      }
    },
    [fetchNotifications, markAllSeen]
  );

  const handleClose = () => setAnchorEl(null);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await markAllRead();
      await fetchNotifications({ variables: { input: { pageInput: { limit: 50 } } } });
    } catch {
      enqueueSnackbar(t('hiring-dashboard-nav:nav.notificationInbox.actionFailed'), { variant: 'error' });
    }
  }, [fetchNotifications, markAllRead]);

  const markNotificationAsRead = useCallback(
    async (notificationId: string) => {
      try {
        await markRead({ variables: { input: { notificationId } } });
      } catch {
        enqueueSnackbar(t('hiring-dashboard-nav:nav.notificationInbox.actionFailed'), { variant: 'error' });
      }
    },
    [markRead]
  );

  const handleToggleRead = useCallback(
    async (notification: InboxNotificationView) => {
      try {
        if (notification.isRead) {
          await markUnread({ variables: { input: { notificationId: notification.id } } });
        } else {
          await markRead({ variables: { input: { notificationId: notification.id } } });
        }
        // Refresh so the header's unreadCount reflects the toggle (the popover has no pagination to preserve).
        await fetchNotifications({ variables: { input: { pageInput: { limit: 50 } } } });
      } catch {
        enqueueSnackbar(t('hiring-dashboard-nav:nav.notificationInbox.actionFailed'), { variant: 'error' });
      }
    },
    [fetchNotifications, markRead, markUnread, t]
  );

  const handleMuteType = useCallback(
    async (notification: InboxNotificationView) => {
      try {
        if (!notification.notificationType) {
          return;
        }
        const labelKey = NOTIFICATION_TYPE_LABEL_KEYS[notification.notificationType];
        const types = getNotificationTypesForLabelKey(labelKey);
        await Promise.all(
          types.map((prefName) =>
            updateNotificationPreference({
              variables: { input: { prefName, medium: EmployeeNotificationMedium.InApp, value: false } },
            })
          )
        );
        enqueueSnackbar(
          t('hiring-dashboard-nav:nav.notificationInbox.typeMuted', { typeLabel: notification.typeLabel }),
          {
            variant: 'success',
            action: (key) => (
              <Button
                onClick={() => {
                  void Promise.all(
                    types.map((prefName) =>
                      updateNotificationPreference({
                        variables: { input: { prefName, medium: EmployeeNotificationMedium.InApp, value: true } },
                      })
                    )
                  );
                  closeSnackbar(key);
                }}
              >
                {t('hiring-dashboard-nav:nav.notificationInbox.undo')}
              </Button>
            ),
          }
        );
      } catch {
        enqueueSnackbar(t('hiring-dashboard-nav:nav.notificationInbox.actionFailed'), { variant: 'error' });
      }
    },
    [t, updateNotificationPreference]
  );

  const handleClickNotification = useCallback(
    (notification: InboxNotificationView) => {
      if (!notification.isRead) {
        void markNotificationAsRead(notification.id);
      }
      if (notification.ctaLink) {
        history.push(notification.ctaLink);
      }
      handleClose();
    },
    [history, markNotificationAsRead]
  );

  const bellIcon = (
    <Badge badgeContent={unseenCount} color="error" max={99}>
      <NotificationIcon />
    </Badge>
  );

  return (
    <>
      {expanded ? (
        <Button
          id="app-nav-notifications"
          data-testid="app-nav-notifications"
          size="small"
          fullWidth
          variant="text"
          onClick={handleOpen}
          startIcon={bellIcon}
          sx={{
            border: 'none',
            display: 'flex',
            justifyContent: 'flex-start',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            borderRadius: '6px',
          }}
        >
          <Typography color="text.primary" variant="body2" fontWeight={500}>
            {t('hiring-dashboard-nav:nav.notificationInbox.title')}
          </Typography>
        </Button>
      ) : (
        <Tooltip title={t('hiring-dashboard-nav:nav.notificationInbox.title')} placement="right">
          <IconButton
            id="app-nav-notifications"
            data-testid="app-nav-notifications"
            onClick={handleOpen}
            sx={{
              height: '28px',
              width: '28px',
              padding: theme.spacing(1),
              borderRadius: '6px',
              border: 'none',
              color: theme.palette.text.primary,
              '&:hover': { backgroundColor: theme.palette.background.contrast, boxShadow: 'none' },
            }}
          >
            {bellIcon}
          </IconButton>
        </Tooltip>
      )}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { elevation: 8, sx: { width: 400, maxHeight: 480, borderRadius: 3, overflow: 'hidden' } } }}
      >
        <NotificationList
          notifications={notifications}
          loading={loading}
          unreadCount={unreadCount}
          onNotificationClick={handleClickNotification}
          onToggleRead={handleToggleRead}
          onMuteType={handleMuteType}
          onMarkAllRead={handleMarkAllRead}
          onOpenSettings={() => {
            history.push(getAccountSettingUrls({ tab: AccountSettingsTab.NOTIFICATIONS }));
            handleClose();
          }}
          onOpenNotifications={() => {
            history.push(Routes.Notifications);
            handleClose();
          }}
        />
      </Popover>
    </>
  );
};

export default NotificationInbox;
